import {
  DEADLINE_TYPES_LIST,
  TASK_DETAILS_LIST,
} from "@/shared/constants/offer-cargo";
import {
  fromIso8601Time,
  isValidDate,
  toIso8601Time,
} from "@/shared/lib/datetime";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseCheckbox } from "@/shared/ui/base-checkbox";
import { BaseFileUploader } from "@/shared/ui/base-file-uploader";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { BaseTimeField } from "@/shared/ui/base-time-field";
import { BaseToggle } from "@/shared/ui/base-toggle";
import { PrefectureField } from "@/widgets/prefecture-field";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import {
  type Control,
  Controller,
  type FormState,
  type UseFieldArrayRemove,
  type UseFormWatch,
  useFieldArray,
} from "react-hook-form";
import type { CreateOfferCargoFormSchemaType } from "../../../model/validation";

const ChildForm = ({
  index,
  totalDestinationCount,
  control,
  watch,
  remove,
}: {
  index: number;
  totalDestinationCount: number;
  remove: UseFieldArrayRemove;
  control: Control<CreateOfferCargoFormSchemaType>;
  watch: UseFormWatch<CreateOfferCargoFormSchemaType>;
}) => {
  const checkedIsLoad = watch(`destinations.${index}.hasLoading`);
  const checkedIsUnload = watch(`destinations.${index}.hasUnloading`);
  const selectedDeadlineType = watch(`destinations.${index}.deadlineType`);
  const title =
    index === 0
      ? "積地"
      : index === totalDestinationCount - 1
        ? "卸地"
        : "経由地";
  const transitNumber =
    index === 0 ? null : index === totalDestinationCount - 1 ? null : index;
  const removable = index !== 0 && index !== totalDestinationCount - 1;
  return (
    <Stack
      spacing={3}
      bgcolor={"grey.50"}
      border="1px solid"
      borderColor={"grey.100"}
      width={"100%"}
      p={2}
    >
      <Typography fontWeight={700}>
        【{title}
        {transitNumber}】
      </Typography>
      <BaseFormLabel label="作業内容" isRequired>
        <Stack direction="row" gap={1}>
          <Controller
            name={`destinations.${index}.hasLoading`}
            control={control}
            render={({ field, fieldState, formState: { isSubmitted } }) => {
              const isEitherChecked = checkedIsLoad || checkedIsUnload;
              const hasError =
                isSubmitted && (fieldState.invalid || !isEitherChecked);
              return (
                <BaseCheckbox
                  label={"積込み"}
                  size="small"
                  {...field}
                  error={hasError}
                  helperText={hasError ? "必須項目です" : undefined}
                />
              );
            }}
          />
          <Controller
            name={`destinations.${index}.hasUnloading`}
            control={control}
            render={({ field, fieldState, formState: { isSubmitted } }) => {
              const isEitherChecked = checkedIsLoad || checkedIsUnload;
              const hasError =
                isSubmitted && (fieldState.invalid || !isEitherChecked);
              return (
                <BaseCheckbox
                  label={"取卸し"}
                  size="small"
                  {...field}
                  error={hasError}
                  helperText={hasError ? "必須項目です" : undefined}
                />
              );
            }}
          />
        </Stack>
      </BaseFormLabel>
      {
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={3}>
          <BaseFormLabel label="時間指定" isRequired>
            <Controller
              name={`destinations.${index}.deadlineType`}
              control={control}
              render={({ field, fieldState }) => (
                <BaseToggle
                  {...field}
                  options={DEADLINE_TYPES_LIST}
                  size="small"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
          <BaseFormLabel label="時間">
            <Box display="flex" gap="10px" alignItems="center">
              <Controller
                name={`destinations.${index}.deadlineTime`}
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <BaseTimeField
                      {...field}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      value={
                        field.value ? fromIso8601Time(field.value) : undefined
                      }
                      onChange={(v) => {
                        field.onChange(
                          isValidDate(v) ? toIso8601Time(v) : undefined,
                        );
                      }}
                    />
                  );
                }}
              />
              {selectedDeadlineType === "term" && (
                <>
                  <Typography justifyItems="Center">–</Typography>
                  <Controller
                    name={`destinations.${index}.deadlineTimeEnd`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <BaseTimeField
                          {...field}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          value={
                            field.value
                              ? fromIso8601Time(field.value)
                              : undefined
                          }
                          onChange={(v) => {
                            field.onChange(
                              isValidDate(v) ? toIso8601Time(v) : undefined,
                            );
                          }}
                        />
                      );
                    }}
                  />
                </>
              )}
            </Box>
          </BaseFormLabel>
        </Box>
      }
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={3}>
        {
          <BaseFormLabel label="郵便番号">
            <Controller
              name={`destinations.${index}.zipCode`}
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
        }
        <BaseFormLabel label="都道府県" isRequired>
          <Controller
            name={`destinations.${index}.prefecture`}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl size="small" fullWidth error={fieldState.invalid}>
                <PrefectureField
                  {...field}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  size="small"
                />
              </FormControl>
            )}
          />
        </BaseFormLabel>
        <BaseFormLabel label="市区町村" isRequired>
          <Controller
            name={`destinations.${index}.city`}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} fullWidth size="small">
                <BaseTextField
                  {...field}
                  size="small"
                  sx={{ width: 256 }}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              </FormControl>
            )}
          />
        </BaseFormLabel>
        {
          <BaseFormLabel label="丁目・番地など" sx={{ maxWidth: "376px" }}>
            <Controller
              name={`destinations.${index}.addressLine`}
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
        }
      </Box>
      {
        <>
          <Box display="flex" gap={3} sx={{ maxWidth: "376px" }}>
            <BaseFormLabel label="建物名" sx={{ width: "100%" }}>
              <Controller
                name={`destinations.${index}.buildingName`}
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </BaseFormLabel>
          </Box>
          <Box display="flex" gap={3} maxWidth="376px">
            <BaseFormLabel label="担当者名">
              <Controller
                name={`destinations.${index}.chargePersonName`}
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    size="small"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </BaseFormLabel>
            <BaseFormLabel label="連絡先">
              <Controller
                name={`destinations.${index}.contactInfo`}
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    size="small"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </BaseFormLabel>
          </Box>
        </>
      }
      {
        <Box display="flex" gap={3} maxWidth="376px">
          <BaseFormLabel label="作業詳細" sx={{ width: "100%" }}>
            <Controller
              name={`destinations.${index}.taskDetail`}
              control={control}
              render={({ field, fieldState }) => (
                <FormControl error={fieldState.invalid} size="small" fullWidth>
                  <BaseSelect
                    {...field}
                    type="string"
                    sx={{ width: 368 }}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    noSelectedOptionText="(選択しない)"
                  >
                    {TASK_DETAILS_LIST.map((task) => (
                      <MenuItem key={task.value} value={task.value}>
                        {task.label}
                      </MenuItem>
                    ))}
                  </BaseSelect>
                </FormControl>
              )}
            />
          </BaseFormLabel>
        </Box>
      }
      {
        <Box display="flex" gap={3} maxWidth="376px">
          <BaseFormLabel label="特記事項(日付など)" sx={{ width: "100%" }}>
            <Controller
              name={`destinations.${index}.memo`}
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  size="small"
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
        </Box>
      }
      {removable && (
        <BaseButton
          variant="outlined"
          color="warning"
          startIcon={<BaseIcon name="trash" color="warning" />}
          sx={{
            bgcolor: "white",
            maxWidth: "88px",
          }}
          onClick={() => {
            remove(index);
          }}
        >
          削除
        </BaseButton>
      )}
    </Stack>
  );
};

export const DestinationsForm = (props: {
  control: Control<CreateOfferCargoFormSchemaType>;
  formState: FormState<CreateOfferCargoFormSchemaType>;
  watch: UseFormWatch<CreateOfferCargoFormSchemaType>;
  onDestinationFilesChange: (files: File[]) => void;
  onDestinationInsertOrRemove: () => void;
}) => {
  const { formState } = props;

  const { fields, insert, remove } = useFieldArray({
    control: props.control,
    name: "destinations",
  });

  return (
    <Accordion
      elevation={0}
      defaultExpanded
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: "16px",
        ":before": {
          backgroundColor: "transparent",
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <BaseIcon
            name="chevronDown"
            htmlColor="#52A48B"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "200px",
            }}
          />
        }
        sx={{
          fontWeight: 700,
        }}
      >
        配送ルート
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={3} mb={2}>
          {fields.map((field, index) => (
            <React.Fragment key={field.id}>
              {index === fields.length - 1 ? (
                <BaseButton
                  key="add-destination"
                  startIcon={<BaseIcon name="plus" htmlColor="#52A48B" />}
                  variant="outlined"
                  onClick={() => {
                    insert(fields.length - 1, {
                      prefecture: "",
                      city: "",
                      deadlineType: "till",
                    });
                    props.onDestinationInsertOrRemove();
                  }}
                >
                  経由地追加
                </BaseButton>
              ) : null}
              <ChildForm
                key={`${field.prefecture}-${field.city}-${field.addressLine}`}
                index={index}
                totalDestinationCount={fields.length}
                remove={(index) => {
                  remove(index);
                  props.onDestinationInsertOrRemove();
                }}
                control={props.control}
                watch={props.watch}
              />
            </React.Fragment>
          ))}
          {formState.errors.destinations?.root?.message && (
            <BaseMessageCard
              status="error"
              message={formState.errors.destinations.root.message}
            />
          )}
        </Stack>
        <BaseFormLabel label="添付ファイル">
          <Box maxWidth={376}>
            <BaseFileUploader
              onChange={(files) => {
                props.onDestinationFilesChange(files);
              }}
            />
          </Box>
        </BaseFormLabel>
      </AccordionDetails>
    </Accordion>
  );
};
